/**
 * Admin API Hooks - React Query hooks per operazioni admin
 *
 * Usa il servizio adminApiService per tutte le operazioni
 * Fornisce caching, invalidation automatica, e optimistic updates
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  adminApi,
  type Client,
  type Recipe,
  type Review,
  type FAQ,
  type ClientFilters,
  type RecipeFilters,
  type ReviewFilters,
  type PaginationParams,
  type DashboardStats,
  type SiteAlert,
  type RiflessioneInput
} from '@/services/adminApiService';
import { supabaseAuth } from '@/lib/supabase-auth';
import { riflessioniQueryKeys } from '@/hooks/useRiflessioni';

// =============================================================================
// QUERY KEYS - Centralized for consistent invalidation
// =============================================================================

export const adminQueryKeys = {
  all: ['admin'] as const,

  // Dashboard
  dashboard: () => [...adminQueryKeys.all, 'dashboard'] as const,
  dashboardStats: () => [...adminQueryKeys.dashboard(), 'stats'] as const,
  recentActivity: () => [...adminQueryKeys.dashboard(), 'activity'] as const,

  // Clients
  clients: () => [...adminQueryKeys.all, 'clients'] as const,
  clientsList: (filters?: ClientFilters) => [...adminQueryKeys.clients(), 'list', filters] as const,
  clientDetail: (id: number) => [...adminQueryKeys.clients(), 'detail', id] as const,

  // Recipes
  recipes: () => [...adminQueryKeys.all, 'recipes'] as const,
  recipesList: (filters?: RecipeFilters) => [...adminQueryKeys.recipes(), 'list', filters] as const,
  recipeDetail: (id: number) => [...adminQueryKeys.recipes(), 'detail', id] as const,

  // Reviews
  reviews: () => [...adminQueryKeys.all, 'reviews'] as const,
  reviewsList: (filters?: ReviewFilters) => [...adminQueryKeys.reviews(), 'list', filters] as const,
  reviewDetail: (id: number) => [...adminQueryKeys.reviews(), 'detail', id] as const,

  // FAQs
  faqs: () => [...adminQueryKeys.all, 'faqs'] as const,
  faqsList: (filters?: PaginationParams) => [...adminQueryKeys.faqs(), 'list', filters] as const,
  faqDetail: (id: number) => [...adminQueryKeys.faqs(), 'detail', id] as const,

  // Client Details
  clientPurchases: (id: number) => [...adminQueryKeys.clients(), 'purchases', id] as const,
  clientCommunications: (id: number) => [...adminQueryKeys.clients(), 'communications', id] as const,

  // Communications
  communications: () => [...adminQueryKeys.all, 'communications'] as const,
  communicationPreview: (id: string) => [...adminQueryKeys.communications(), 'preview', id] as const,

  // Press
  press: () => [...adminQueryKeys.all, 'press'] as const,
  pressArticles: (filters?: PaginationParams) => [...adminQueryKeys.press(), 'articles', filters] as const,
  pressPublications: () => [...adminQueryKeys.press(), 'publications'] as const,

  // Riflessioni
  riflessioni: () => [...adminQueryKeys.all, 'riflessioni'] as const,

  // Site Alerts
  alerts: () => [...adminQueryKeys.all, 'alerts'] as const,
  activeAlert: () => [...adminQueryKeys.all, 'alerts', 'active'] as const,
};

// =============================================================================
// DASHBOARD HOOKS
// =============================================================================

export function useAdminDashboardStats() {
  return useQuery({
    queryKey: adminQueryKeys.dashboardStats(),
    queryFn: adminApi.getDashboardStats,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refresh every minute
  });
}

export function useAdminRecentActivity(limit = 10) {
  return useQuery({
    queryKey: adminQueryKeys.recentActivity(),
    queryFn: () => adminApi.getRecentActivity(limit),
    staleTime: 30 * 1000,
  });
}

// =============================================================================
// CLIENT HOOKS
// =============================================================================

export function useAdminClients(filters?: ClientFilters) {
  return useQuery({
    queryKey: adminQueryKeys.clientsList(filters),
    queryFn: () => adminApi.getClients(filters || {}),
    staleTime: 60 * 1000,
  });
}

export function useRicettarioOnlyClientIds() {
  return useQuery({
    queryKey: [...adminQueryKeys.clients(), 'ricettario-only-ids'],
    queryFn: adminApi.getRicettarioOnlyClientIds,
    staleTime: 5 * 60 * 1000, // 5 minuti — questi dati cambiano raramente
  });
}

export function useAdminClient(id: number) {
  return useQuery({
    queryKey: adminQueryKeys.clientDetail(id),
    queryFn: () => adminApi.getClientById(id),
    enabled: !!id,
  });
}

export function useCreateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Client>) => adminApi.createClient(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.clients() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.dashboardStats() });
    },
  });
}

export function useUpdateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Client> }) =>
      adminApi.updateClient(id, data),
    onSuccess: (_, variables) => {
      // Invalidate specific client and list
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.clientDetail(variables.id) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.clients() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.dashboardStats() });
    },
  });
}

export function useDeleteClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminApi.deleteClient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.clients() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.dashboardStats() });
    },
  });
}

/**
 * Hook for fetching client purchase history
 * Fetches recipes, products, and consultation purchases
 */
export function useClientPurchases(clientId: number) {
  return useQuery({
    queryKey: adminQueryKeys.clientPurchases(clientId),
    queryFn: async () => {
      const session = supabaseAuth.getSession();
      const response = await fetch(`/api/admin/clients/${clientId}/purchases`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.id && { 'X-Admin-Id': session.id }),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch purchases');
      return response.json();
    },
    enabled: !!clientId,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook for fetching client communication history
 * Returns emails sent to this client
 */
export function useClientCommunications(clientId: number) {
  return useQuery({
    queryKey: adminQueryKeys.clientCommunications(clientId),
    queryFn: async () => {
      const session = supabaseAuth.getSession();
      const response = await fetch(`/api/admin/clients/${clientId}/communications`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.id && { 'X-Admin-Id': session.id }),
        },
      });
      if (!response.ok) {
        if (response.status === 404) {
          return { communications: [], total: 0 };
        }
        throw new Error('Failed to fetch communications');
      }
      return response.json();
    },
    enabled: !!clientId,
    staleTime: 30 * 1000,
  });
}

// =============================================================================
// RECIPE HOOKS
// =============================================================================

export function useAdminRecipes(filters?: RecipeFilters) {
  return useQuery({
    queryKey: adminQueryKeys.recipesList(filters),
    queryFn: () => adminApi.getRecipes(filters || {}),
    staleTime: 60 * 1000,
  });
}

export function useAdminRecipe(id: number) {
  return useQuery({
    queryKey: adminQueryKeys.recipeDetail(id),
    queryFn: () => adminApi.getRecipeById(id),
    enabled: !!id,
  });
}

export function useCreateRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Omit<Recipe, 'id' | 'created_at' | 'updated_at'>) =>
      adminApi.createRecipe(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.recipes() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.dashboardStats() });
    },
  });
}

export function useUpdateRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Recipe> }) =>
      adminApi.updateRecipe(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.recipeDetail(variables.id) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.recipes() });
    },
  });
}

export function useDeleteRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminApi.deleteRecipe(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.recipes() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.dashboardStats() });
    },
  });
}

// =============================================================================
// REVIEW HOOKS
// =============================================================================

export function useAdminReviews(filters?: ReviewFilters) {
  return useQuery({
    queryKey: adminQueryKeys.reviewsList(filters),
    queryFn: () => adminApi.getReviews(filters || {}),
    staleTime: 30 * 1000,
  });
}

export function useAdminReview(id: number) {
  return useQuery({
    queryKey: adminQueryKeys.reviewDetail(id),
    queryFn: () => adminApi.getReviewById(id),
    enabled: !!id,
  });
}

export function useCreateReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Omit<Review, 'id' | 'created_at' | 'updated_at'>) =>
      adminApi.createReview(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.reviews() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.dashboardStats() });
    },
  });
}

export function useUpdateReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Review> }) =>
      adminApi.updateReview(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.reviews() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.dashboardStats() });
    },
  });
}

export function useApproveReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminApi.approveReview(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.reviewDetail(id) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.reviews() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.dashboardStats() });
    },
  });
}

export function useRejectReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminApi.rejectReview(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.reviewDetail(id) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.reviews() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.dashboardStats() });
    },
  });
}

export function useDeleteReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminApi.deleteReview(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.reviews() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.dashboardStats() });
    },
  });
}

// =============================================================================
// FAQ HOOKS
// =============================================================================

export function useAdminFaqs(filters?: PaginationParams) {
  return useQuery({
    queryKey: adminQueryKeys.faqsList(filters),
    queryFn: () => adminApi.getFaqs(filters || {}),
    staleTime: 0,
  });
}

export function useAdminFaq(id: number) {
  return useQuery({
    queryKey: adminQueryKeys.faqDetail(id),
    queryFn: () => adminApi.getFaqById(id),
    enabled: !!id,
  });
}

export function useCreateFaq() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Omit<FAQ, 'id' | 'created_at' | 'updated_at'>) =>
      adminApi.createFaq(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.faqs() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.dashboardStats() });
    },
  });
}

export function useUpdateFaq() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<FAQ> }) =>
      adminApi.updateFaq(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.faqDetail(variables.id) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.faqs() });
    },
  });
}

export function useDeleteFaq() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminApi.deleteFaq(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.faqs() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.dashboardStats() });
    },
  });
}

// =============================================================================
// PRESS HOOKS
// =============================================================================

export function useAdminPressArticles(filters?: PaginationParams) {
  return useQuery({
    queryKey: adminQueryKeys.pressArticles(filters),
    queryFn: () => adminApi.getPressArticles(filters || {}),
    staleTime: 60 * 1000,
  });
}

export function useAdminPressPublications() {
  return useQuery({
    queryKey: adminQueryKeys.pressPublications(),
    queryFn: adminApi.getPressPublications,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// =============================================================================
// RIFLESSIONI
// =============================================================================

export function useAdminRiflessioni() {
  return useQuery({
    queryKey: adminQueryKeys.riflessioni(),
    queryFn: adminApi.getRiflessioni,
    staleTime: 60 * 1000,
  });
}

// Dopo ogni modifica va aggiornato anche il sito pubblico, non solo l'elenco del gestionale
function useInvalidateRiflessioni() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: adminQueryKeys.riflessioni() });
    queryClient.invalidateQueries({ queryKey: riflessioniQueryKeys.all });
  };
}

export function useCreateRiflessione() {
  const invalidate = useInvalidateRiflessioni();
  return useMutation({
    mutationFn: (riflessione: RiflessioneInput) => adminApi.createRiflessione(riflessione),
    onSuccess: invalidate,
  });
}

export function useUpdateRiflessione() {
  const invalidate = useInvalidateRiflessioni();
  return useMutation({
    mutationFn: ({ id, updates }: { id: number; updates: Partial<RiflessioneInput> }) =>
      adminApi.updateRiflessione(id, updates),
    onSuccess: invalidate,
  });
}

export function useDeleteRiflessione() {
  const invalidate = useInvalidateRiflessioni();
  return useMutation({
    mutationFn: (id: number) => adminApi.deleteRiflessione(id),
    onSuccess: invalidate,
  });
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook to invalidate all admin queries (useful after bulk operations)
 */
export function useInvalidateAdminQueries() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: adminQueryKeys.all });
  };
}

/**
 * Hook for client counts (useful for stats cards)
 */
export function useClientCount() {
  const { data } = useAdminDashboardStats();
  return {
    total: data?.totalClients || 0,
    active: data?.activeClients || 0,
  };
}

/**
 * Hook for recipe counts
 */
export function useRecipeCount() {
  const { data } = useAdminDashboardStats();
  return {
    total: data?.totalRecipes || 0,
    published: data?.publishedRecipes || 0,
  };
}

/**
 * Hook for pending reviews count
 */
export function usePendingReviewsCount() {
  const { data } = useAdminDashboardStats();
  return data?.pendingReviews || 0;
}

// =============================================================================
// EMAIL PREVIEW HOOKS
// =============================================================================

export interface EmailPreviewResponse {
  html: string | null;
  fallback: boolean;
  message?: string;
  communication: {
    id: string;
    subject: string | null;
    sent_at: string;
    recipient_email: string;
    recipient_name: string | null;
    communication_type: string;
    attachments: Array<{ name: string; url: string; type: string }>;
  };
}

/**
 * Hook for fetching email preview HTML
 * Reconstructs email HTML from stored template parameters
 *
 * @param communicationId - ID of the communication to preview (null to disable)
 */
export function useEmailPreview(communicationId: string | null) {
  return useQuery({
    queryKey: adminQueryKeys.communicationPreview(communicationId || ''),
    queryFn: async (): Promise<EmailPreviewResponse> => {
      const session = supabaseAuth.getSession();
      const response = await fetch(`/api/admin/communications/${communicationId}/preview`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.id && { 'X-Admin-Id': session.id }),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch email preview');
      }

      return response.json();
    },
    enabled: !!communicationId,
    staleTime: Infinity, // HTML ricostruito è sempre uguale per gli stessi params
    gcTime: 10 * 60 * 1000, // Cache for 10 minutes
  });
}

/**
 * Hook for resending a historical email to the same recipient.
 * The backend rebuilds the HTML from stored template params and logs the resend.
 */
export function useResendCommunication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (communicationId: string): Promise<{ success: boolean; message: string }> => {
      const session = supabaseAuth.getSession();
      const response = await fetch(`/api/admin/communications/${communicationId}/resend`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.id && { 'X-Admin-Id': session.id }),
        },
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Errore nel reinvio della email');
      }
      return data;
    },
    onSuccess: () => {
      // Il reinvio crea un nuovo record: aggiorna gli storici comunicazioni
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.clients() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.communications() });
    },
  });
}

// =============================================================================
// SITE ALERT HOOKS
// =============================================================================

export function useAdminAlerts() {
  return useQuery({
    queryKey: adminQueryKeys.alerts(),
    queryFn: adminApi.getSiteAlerts,
    staleTime: 30 * 1000,
  });
}

export function useActiveAlert() {
  return useQuery({
    queryKey: adminQueryKeys.activeAlert(),
    queryFn: adminApi.getActiveSiteAlert,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<SiteAlert>) => adminApi.createSiteAlert(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.alerts() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.activeAlert() });
    },
  });
}

export function useUpdateAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<SiteAlert> }) =>
      adminApi.updateSiteAlert(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.alerts() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.activeAlert() });
    },
  });
}

export function useDeleteAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminApi.deleteSiteAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.alerts() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.activeAlert() });
    },
  });
}
